import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import EmployeeMap from '../components/EmployeeMap';
import EmployeeList from '../components/EmployeeList';
import { useEmployee } from '../contexts/EmployeeContext';
import { MapOrListView } from '../types/views';
import { Map as MapIcon, Users, ArrowUpRight, Globe as Globe2, Building2, Languages } from 'lucide-react';
import { normalizeCountry } from '../utils/countryUtils';
import { tzLabel } from '../utils/geoUtils';

const HomePage = () => {
  const { filteredEmployees, isLoading, error } = useEmployee();
  const [activeView, setActiveView] = useState<MapOrListView>('map');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Global Team Directory - World Map';
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-sm text-bolt-textMuted">Loading team data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-500/10 border border-error-500/30 text-error-400 px-4 py-3 rounded-lg" role="alert">
        <strong className="font-semibold">Error:</strong>
        <span className="ml-1">{error}</span>
      </div>
    );
  }

  const uniqueCountries = new Set(filteredEmployees.map(e => normalizeCountry(e.location.country)).filter(Boolean)).size;
  const uniqueTimezones = new Set(filteredEmployees.map(e => tzLabel(e.location.timezone))).size;
  const uniqueDepartments = Array.from(new Set(filteredEmployees.map(e => e.department))).length;
  const uniqueLanguages = Array.from(new Set(filteredEmployees.flatMap(e => e.languages))).length;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="section-header mb-2">Team Directory</p>
          <h1 className="text-2xl font-bold text-bolt-text mb-1">StackBlitz Team</h1>
          <p className="text-sm text-bolt-textMuted">
            {filteredEmployees.length} members across {uniqueCountries} {uniqueCountries === 1 ? 'country' : 'countries'}
          </p>
        </div>

        {/* View toggle */}
        <div className="flex items-center bg-bolt-bg border border-bolt-border rounded-lg p-0.5 gap-0.5">
          <button
            className={`px-3 py-1.5 flex items-center gap-1.5 rounded-md text-sm font-medium transition-all duration-150 ${
              activeView === 'map'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover'
            }`}
            onClick={() => setActiveView('map')}
          >
            <MapIcon className="h-3.5 w-3.5" />
            <span>Map</span>
          </button>
          <button
            className={`px-3 py-1.5 flex items-center gap-1.5 rounded-md text-sm font-medium transition-all duration-150 ${
              activeView === 'list'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover'
            }`}
            onClick={() => setActiveView('list')}
          >
            <Users className="h-3.5 w-3.5" />
            <span>List</span>
          </button>
        </div>
      </div>

      {filteredEmployees.length === 0 && (
        <div className="bg-warning-500/10 border border-warning-500/25 text-warning-400 px-4 py-3 rounded-lg text-sm">
          No employees match your search. Try different search terms.
        </div>
      )}

      {/* Map or List */}
      <div>
        {activeView === 'map' ? (
          <EmployeeMap employees={filteredEmployees} />
        ) : (
          <EmployeeList employees={filteredEmployees} />
        )}
      </div>

      {/* Team Insights */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <Link to="/insights" className="text-base font-semibold text-bolt-text hover:text-primary-400 transition-colors">Team Insights</Link>
          <span className="section-header">Overview</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <InsightCard
            icon={<Globe2 className="h-4 w-4" />}
            title="Time Zones"
            value={`${uniqueTimezones} time zones`}
            description="Spanning the globe"
            onClick={() => navigate('/insights')}
          />
          <InsightCard
            icon={<Building2 className="h-4 w-4" />}
            title="Departments"
            value={`${uniqueDepartments} departments`}
            description="Across the organization"
            onClick={() => navigate('/insights')}
          />
          <InsightCard
            icon={<Languages className="h-4 w-4" />}
            title="Languages"
            value={`${uniqueLanguages} languages`}
            description="Spoken by team members"
            onClick={() => navigate('/insights')}
          />
        </div>
      </div>
    </div>
  );
};

interface InsightCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  description: string;
  onClick: () => void;
}

const InsightCard = ({ icon, title, value, description, onClick }: InsightCardProps) => (
  <button
    onClick={onClick}
    className="bg-bolt-bg border border-bolt-border rounded-lg p-4 hover:border-primary-600/60 hover:bg-bolt-surfaceHover transition-all duration-150 group text-left w-full"
  >
    <div className="flex items-center gap-2 mb-3">
      <div className="p-1.5 bg-primary-600/15 rounded-md text-primary-400 group-hover:bg-primary-600/25 transition-colors">
        {icon}
      </div>
      <span className="text-xs font-medium text-bolt-textMuted uppercase tracking-wide">{title}</span>
    </div>
    <p className="text-lg font-semibold text-bolt-text mb-0.5">{value}</p>
    <p className="text-xs text-bolt-textSubtle mb-3">{description}</p>
    <span className="inline-flex items-center gap-1 text-xs text-primary-400 group-hover:text-primary-300 font-medium transition-colors">
      View details
      <ArrowUpRight className="h-3 w-3" />
    </span>
  </button>
);

export default HomePage;
